import type {
  InitPlayerModule,
  WebediaPlaylistItem
} from '@webedia-services/webedia-player';
import webediaPlayer from '@webedia-services/webedia-player';
import initPlayerModule, {
  WebediaDailymotionPlayer
} from '@webedia-services/webedia-player/dailymotion';
import type {
  DailymotionInitPlayerModule,
  DailymotionOptions
} from '@webedia-services/webedia-player/dailymotion';

import { getConsentState } from 'common/consent/Tcf';
import { DM_PREMIUM_KEYWORD } from 'common/constants/Ads';
import { getPostrollAsVideoConfiguration } from 'common/module/advertisement';
import { PostrollAsVideoConfig } from 'common/module/advertisement/types';
import eventEmitter, { EventsTypes } from 'common/services/events/eventEmitter';
import { hasAdblock } from 'common/tools/adblock';
import { addClass, removeClass } from 'common/tools/dom/classes';
import { transPlayer } from 'common/tools/translations/trans';
import { isHTMLElement, isString } from 'common/types';

import type { PlayerCustomData } from 'website/module/player';
import { getAdvertisingConfiguration } from 'website/module/player/utils';
import initTrackingPlayer from 'website/tracking/player';

let playerCount = 0; // used to create a unique ID for each player on the page

const setPlayerDefaultConfig = async (
  playerContainer: HTMLElement,
  customData: PlayerCustomData
) => {
  const target = playerContainer.getElementsByClassName('js-media-player')[0];
  if (target.id === '') {
    // if there is no ID, dailymotion will display warnings on the console
    playerCount += 1;
    target.id = 'allocine-player-' + playerCount;
  }
  const videos = customData.videos;
  if (!videos) {
    return {
      customData,
      playerOptions: null,
      playlist: [],
      postrollConfig: { active: false },
      target
    };
  }

  const params = customData.smartMute ? {} : { params: { mute: true } };

  const playerOptions: DailymotionOptions = {
    player:
      customData.playerDmId ?? window.AC.config.player.dailymotionPlayerId,
    keywordDisableAd: DM_PREMIUM_KEYWORD,
    timeoutPRT: 75,
    ...params,
    referrerPolicy: 'no-referrer-when-downgrade'
  };

  const PRT_CONFIG: PostrollAsVideoConfig =
    (await getPostrollAsVideoConfiguration(customData)) ?? {};

  const playlist = videos.reduce<WebediaPlaylistItem[]>((acc, video) => {
    if (video.idDailymotion) {
      acc.push({
        mediaid: video.idDailymotion,
        title: video.title ?? undefined
      });
    }
    return acc;
  }, []);

  return {
    customData,
    playerOptions,
    playlist,
    postrollConfig: {
      active:
        typeof customData.disablePostroll === 'undefined'
          ? true
          : !customData.disablePostroll,
      previewMessage: transPlayer('video-player.preview-message'),
      titleReleaseDate: transPlayer('video-player.title-release-date'),
      ...PRT_CONFIG
    },
    target
  };
};

/**
 * Allows dynamic creation of a player
 */
export const createPlayer = async (
  playerContainer: HTMLElement,
  data: PlayerCustomData
) => {
  // we have to wait for the consent before start a player thanks a lot CNIL...
  await getConsentState(true);

  const { customData, playerOptions, playlist, postrollConfig, target } =
    await setPlayerDefaultConfig(playerContainer, data);

  if (!playerOptions) {
    return;
  }

  const adBlock = hasAdblock();

  if (initPlayerModule && isHTMLElement(target)) {
    const webediaPlayerInstance = await webediaPlayer(
      target,
      initPlayerModule as InitPlayerModule<DailymotionInitPlayerModule>,
      {
        playerOptions,
        playlist: adBlock ? playlist : undefined,
        postrollConfig
      }
    );

    if (webediaPlayerInstance) {
      webediaPlayerInstance.addEventListener('autostart-not-allowed', () => {
        removeClass(playerContainer, 'player-auto-play');
      });

      webediaPlayerInstance.addEventListener('video-start', () => {
        addClass(playerContainer, 'video-started');
      });

      webediaPlayerInstance.addEventListener('player-start', () => {
        addClass(playerContainer, 'player-started');
      });

      webediaPlayerInstance.addEventListener('ready', () => {
        eventEmitter.emit(
          EventsTypes.PLAYER_READY,
          webediaPlayerInstance?.getPlayerId() ?? ''
        );
      });

      let customParams;

      if (!adBlock) {
        // Because of the rules of restriction on the heavy
        // advertisements of Google Chrome, we cut the
        // loading of the Webedia Player as below.
        //
        // 1.
        // Load the ad server script AFTER loading the
        // Webedia Player.
        const advertisingData = await getAdvertisingConfiguration(customData);

        const player =
          webediaPlayerInstance.getPlayer() as WebediaDailymotionPlayer;

        webediaPlayerInstance.setPlaylist(playlist);

        customParams = advertisingData?.disablePreroll
          ? DM_PREMIUM_KEYWORD
          : isString(advertisingData?.tag)
            ? advertisingData?.tag
            : undefined;

        // 2.
        // update values for advertising
        player.setCustomConfig({
          customParams
        });

        // 2.5
        // check if player is autoplay, if not setMute to false
        const player_is_not_autoplay =
          player?.getSettings()?.autostart === 'off';

        if (player_is_not_autoplay) {
          player?.setMute(false);
        }

        // 3.
        // load a video id in the preloaded player
        player.loadContent({
          video: playlist.length ? playlist[0].mediaid : undefined
        });
      }

      initTrackingPlayer(webediaPlayerInstance, customData, 'dailymotion', {
        customParams,
        postrollConfig
      });
    }
  }
};
